import {
    DataTypeSpec,
    DataTypesSpec,
    SUPPORTED_SYSTEM_DATA_TYPES,
    SystemSourceTypes,
    SystemType,
} from 'app/shared/collections/system_type';
import { ImsObject } from './ims_object';
import { CensoredSystemResource } from '../resources';

export const IMS_TYPE__CENSORED_SYSTEM = 'censored_system';

export const CENSORED_SYSTEM_SENSITIVE_FIELDS = [
    'auth_password',
    'community_string',
    'community_strings',
    'password',
    'passwords',
    'privacy_password',
];

interface RemoteSystemConfig {
    add_to_dynamic_devices?: boolean;
}

interface SystemConfig {
    remote_state_configuration?: {
        [x: string]: RemoteSystemConfig;
    };
}

interface ICensoredSystemResource {
    id: number | null;
    name: string | null;
    type_id: number;
    description: string | null;
    enabled_data_types: DataTypeSpec | null;
    config: SystemConfig | null;
    is_deleted: boolean;
    last_updated: string;
    site_ids: Array<number> | null;
    license_allowed_system_type: boolean;
    license_allowed_integration_count: number | null;
    license_current_integration_count: number;
}

export class CensoredSystem extends ImsObject {
    public type: SystemType;

    constructor(resource: ICensoredSystemResource = new CensoredSystemResource()) {
        super(IMS_TYPE__CENSORED_SYSTEM, resource);
    }

    get name(): string {
        return this.resource.name;
    }

    set name(value: string) {
        this.resource.name = value;
    }

    get config(): SystemConfig {
        return this.resource.config;
    }

    set config(value: SystemConfig) {
        this.resource.config = value;
    }

    get description(): string | null {
        return this.resource.description;
    }

    set description(value: string | null) {
        this.resource.description = value;
    }

    get enabled_data_types(): DataTypesSpec {
        // Sometimes enabled_data_types could be null in the database.
        // Protect the frontend from that!
        return this.resource.enabled_data_types || {};
    }

    /**
     * This function looks up the enabled data types dictionary. If there is no appropriate
     * entry, the defaults from the parent system type will be used.
     * @param typeName
     */
    getEnabledDataTypeSpec(typeName: string): DataTypeSpec {
        if (!SUPPORTED_SYSTEM_DATA_TYPES.includes(typeName)) {
            throw new Error(`Unsupported data type ${typeName}`);
        }
        let systemTypeDataSpec;
        if (!this.type) {
            console.error(
                'type is falsey, probably because the system types have not yet been loaded, using empty spec',
            );
            systemTypeDataSpec = {};
        } else {
            systemTypeDataSpec = this.type.getDataTypeSpec(typeName);
        }

        const enabledDataSpec = this.enabled_data_types[typeName];
        if (enabledDataSpec === null) {
            // If set to null, the user is explicitly disabling this integration.
            // Return null for the spec
            return null;
        }

        if (systemTypeDataSpec === undefined && enabledDataSpec === undefined) {
            // The data type does not exist in the system_type nor the system.
            // It's disabled!
            return null;
        }

        return Object.assign({}, systemTypeDataSpec, enabledDataSpec);
    }

    /**
     * Handy helper for extracting the source type from the enabled data type dict, for a given type,
     * after applying defaults from the parent system type.
     * @param typeName
     */
    getEnabledDataTypeSourceType(typeName: string): string {
        const enabledDataTypeSpec = this.getEnabledDataTypeSpec(typeName);
        if (enabledDataTypeSpec) {
            return enabledDataTypeSpec.source_type;
        } else {
            return null;
        }
    }

    hasEnabledDataType(typeName: string): boolean {
        return !!this.getEnabledDataTypeSpec(typeName);
    }

    /**
     * For this system, can the asset integration be linked (i.e. auto mode) to the enabling / disabling
     * of the IP device integration
     */
    canAssetIntegrationBeLinkedToIPDeviceIntegration(): boolean {
        return (
            this.hasEnabledDataType('asset') &&
            this.hasEnabledDataType('ip_device') &&
            this.getEnabledDataTypeSourceType('asset') !== SystemSourceTypes.NonChildIpDevice
        );
    }

    get isDeleted(): boolean {
        return this.resource.is_deleted;
    }

    set isDeleted(value: boolean) {
        this.resource.is_deleted = value;
    }

    get last_updated(): string {
        return this.resource.last_updated;
    }

    get site_ids(): Array<number> {
        return this.resource.site_ids;
    }

    get type_id(): number {
        return this.resource.type_id;
    }

    set type_id(value: number) {
        this.resource.type_id = value;
    }

    get license_allowed_system_type(): boolean {
        return this.resource.license_allowed_system_type;
    }

    get license_allowed_integration_count(): number | null {
        return this.resource.license_allowed_integration_count;
    }

    get license_current_integration_count(): number {
        return this.resource.license_current_integration_count;
    }

    //siteId get / set exist just to provide compatibility with existing form behaviours which expect
    //a siteId property
    get siteId() {
        return undefined;
    }

    set siteId(value: number | undefined) {
        //deliberately ignored, see above
    }
}
